import { useState } from "react";
import { useJobStore } from "../../stores/jobStore";
import { JobListItem, JobStatus } from "../../api/client";

const STATUS_CONFIG: Record<JobStatus, { dot: string; label: string; text: string }> = {
  QUEUED:               { dot: "bg-slate-500",  label: "Queued",         text: "text-slate-400" },
  RUNNING:              { dot: "bg-blue-500 animate-pulse",   label: "Scanning",   text: "text-blue-400" },
  INTAKE_COMPLETE:      { dot: "bg-cyan-500",   label: "Review",         text: "text-cyan-400" },
  CATEGORIZED:          { dot: "bg-teal-500",   label: "Categorized",    text: "text-teal-400" },
  NEEDS_HUMAN_APPROVAL: { dot: "bg-amber-500 animate-pulse",  label: "HITL",       text: "text-amber-400" },
  RESUMING:             { dot: "bg-violet-500 animate-pulse", label: "Resuming",   text: "text-violet-400" },
  COMPLETED:            { dot: "bg-emerald-500", label: "Done",          text: "text-emerald-400" },
  FAILED:               { dot: "bg-red-500",     label: "Failed",        text: "text-red-400" },
  AI_INTAKE_PAUSED:     { dot: "bg-orange-500",  label: "AI Paused",     text: "text-orange-400" },
};

function JobRow({ job, selected, onClick }: { job: JobListItem; selected: boolean; onClick: () => void }) {
  const { deleteJob } = useJobStore();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const cfg = STATUS_CONFIG[job.status] ?? STATUS_CONFIG.QUEUED;
  const time = new Date(job.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirming) { setConfirming(true); return; }
    setDeleting(true);
    try { await deleteJob(job.id); } finally { setDeleting(false); setConfirming(false); }
  };

  return (
    <div className="relative group">
      <button
        onClick={onClick}
        className={[
          "w-full text-left px-3 py-3 rounded-lg border transition-all duration-150 cursor-pointer",
          "flex flex-col gap-1.5",
          selected
            ? "bg-rdp-active border-blue-600/60 shadow-glow-blue"
            : "bg-rdp-card border-rdp-border hover:bg-rdp-hover hover:border-rdp-hover",
        ].join(" ")}
      >
        {/* Filename */}
        <span className="text-sm font-medium text-white truncate block pr-6" title={job.source_filename}>
          {job.source_filename}
        </span>

        {/* Meta row */}
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            <span className={`text-xs font-medium ${cfg.text}`}>{cfg.label}</span>
          </span>

          <div className="flex items-center gap-2 flex-shrink-0">
            {job.pending_hitl_count > 0 && (
              <span className="text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded-full">
                {job.pending_hitl_count} HITL
              </span>
            )}
            <span className="text-[10px] text-slate-500 font-mono">
              {job.chart_count} chart{job.chart_count !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Time */}
        <span className="text-[10px] text-slate-600 font-mono">{time}</span>
      </button>

      {/* Delete button — appears on hover */}
      <button
        onClick={handleDelete}
        disabled={deleting}
        title={confirming ? "Click again to confirm delete" : "Delete job"}
        className={[
          "absolute top-2 right-2 w-6 h-6 rounded flex items-center justify-center transition-all z-20",
          "opacity-40 hover:opacity-100",
          confirming
            ? "bg-red-600/80 text-white opacity-100"
            : "bg-rdp-hover text-slate-500 hover:bg-red-600/60 hover:text-white",
          deleting ? "opacity-50 cursor-wait" : "cursor-pointer",
        ].join(" ")}
      >
        {deleting ? (
          <span className="w-3 h-3 rounded-full border border-current border-t-transparent animate-spin block" />
        ) : confirming ? (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        )}
      </button>

      {/* Click-away to cancel confirm */}
      {confirming && (
        <div className="fixed inset-0 z-10" onClick={() => setConfirming(false)} />
      )}
    </div>
  );
}

export function JobsTable() {
  const { jobs, jobsLoading, selectedJob, selectJob } = useJobStore();

  const hitlJobs = jobs.filter((j) => j.pending_hitl_count > 0);
  const otherJobs = jobs.filter((j) => j.pending_hitl_count === 0);

  if (jobsLoading && !jobs.length) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-5 h-5 rounded-full border-2 border-rdp-border border-t-blue-500 animate-spin" />
      </div>
    );
  }

  if (!jobs.length) {
    return (
      <div className="flex flex-col items-center justify-center h-32 gap-2 text-slate-600">
        <span className="text-2xl">📭</span>
        <span className="text-xs">No jobs yet</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 p-2">
      {/* HITL priority queue */}
      {hitlJobs.length > 0 && (
        <>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-500/70 px-1 pt-1 pb-0.5">
            ⚡ Needs Review
          </p>
          {hitlJobs.map((j) => (
            <JobRow key={j.id} job={j} selected={selectedJob?.id === j.id} onClick={() => selectJob(j.id)} />
          ))}
          <div className="border-t border-rdp-border my-1" />
        </>
      )}

      {/* All other jobs */}
      {hitlJobs.length > 0 && (
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 px-1 pb-0.5">
          All Jobs
        </p>
      )}
      {otherJobs.map((j) => (
        <JobRow key={j.id} job={j} selected={selectedJob?.id === j.id} onClick={() => selectJob(j.id)} />
      ))}
    </div>
  );
}
